import React, { createContext, useReducer } from 'react';

type SelectableTable = {
  option: 'unselected' | 'all';
  exclude: Array<string | number>;
  include: Array<string | number>;
  pages: Record<number, number>;
};

type UIContextOptions = {
  sidebarIsOpen: boolean;
  selectableTable: SelectableTable;
  bulkUpdateData: any;
  bulkUpdateCounter: number;
  isOpenBulkUpdateModal: boolean;
  setBulkUpdateModal: (payload: any) => void;
  setBulkUpdateCounter: (payload: any) => void;
  setBulkUpdateData: (payload: any) => void;
  setSelection: (payload: any) => void;
  clearBulkUpdateState: () => void;
  setSidebarIsOpen: (open: boolean) => void;
};

const initialState: UIContextOptions = {
  sidebarIsOpen: false,
  selectableTable: {
    option: 'unselected',
    exclude: [],
    include: [],
    pages: {},
  },
  bulkUpdateData: undefined,
  bulkUpdateCounter: 0,
  isOpenBulkUpdateModal: false,
  setBulkUpdateModal: (_payload) => {},
  setBulkUpdateCounter: (_payload) => {},
  setBulkUpdateData: (_payload) => {},
  setSelection: (_payload) => {},
  clearBulkUpdateState: () => {},

  setSidebarIsOpen: (_open) => {},
};

export const UIContext = createContext<UIContextOptions>(initialState);

const SET_SIDEBAR_IS_OPEN = 'SET_SIDEBAR_IS_OPEN';
const SET_SELECTED_TABLE = 'SET_SELECTED_TABLE';
const SET_BULK_UPDATE_DATA = 'SET_BULK_UPDATE_DATA';
const SET_BULK_UPDATE_COUNTER = 'SET_BULK_UPDATE_COUNTER';
const SET_BULK_UPDATE_MODAL = 'SET_BULK_UPDATE_MODAL';
const CLEAR_BULK_UPDATE_STATE = 'CLEAR_BULK_UPDATE_STATE';

function reducer(state: UIContextOptions, action: any) {
  const { type, payload } = action;

  switch (type) {
    case SET_SIDEBAR_IS_OPEN:
      return { ...state, sidebarIsOpen: payload.sidebarIsOpen };
    case SET_BULK_UPDATE_MODAL:
      return { ...state, isOpenBulkUpdateModal: payload.isOpenBulkUpdateModal };
    case SET_SELECTED_TABLE:
      return {
        ...state,
        selectableTable: {
          ...state.selectableTable,
          ...payload,
        },
      };
    case SET_BULK_UPDATE_DATA:
      return {
        ...state,
        bulkUpdateData: payload,
      };
    case SET_BULK_UPDATE_COUNTER:
      return {
        ...state,
        bulkUpdateCounter: payload,
      };
    case CLEAR_BULK_UPDATE_STATE:
      return {
        ...state,
        bulkUpdateCounter: 0,
        selectableTable: {
          option: 'unselected',
          exclude: [],
          include: [],
          pages: {},
        },
        isOpenBulkUpdateModal: false,
      };
    default:
      return state;
  }
}

type UIProviderProps = { children: React.ReactNode };

const UIProvider = (props: UIProviderProps) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  const setSidebarIsOpen = (sidebarIsOpen: any) =>
    dispatch({
      type: SET_SIDEBAR_IS_OPEN,
      payload: { sidebarIsOpen },
    });

  const setBulkUpdateData = (payload: any) =>
    dispatch({
      type: SET_BULK_UPDATE_DATA,
      payload,
    });

  const setBulkUpdateModal = (payload: any) =>
    dispatch({
      type: SET_BULK_UPDATE_MODAL,
      payload,
    });

  const setBulkUpdateCounter = (payload: any) =>
    dispatch({
      type: SET_BULK_UPDATE_COUNTER,
      payload,
    });

  const setSelection = (payload: any) =>
    dispatch({
      type: SET_SELECTED_TABLE,
      payload,
    });

  const clearBulkUpdateState = () =>
    dispatch({
      type: CLEAR_BULK_UPDATE_STATE,
    });

  return (
    <UIContext.Provider
      value={{
        ...state,
        clearBulkUpdateState,
        setBulkUpdateModal,
        setBulkUpdateCounter,
        setBulkUpdateData,
        setSelection,
        setSidebarIsOpen,
      }}
    >
      {children}
    </UIContext.Provider>
  );
};

export default UIProvider;
